import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '@/api';
import { ResidentListHeader } from '@/components/residentListHeader';
import { formatUtcFetchTimestamp } from '@/utils/dateutil';
import { residentListSettingsSelector, residentListSettingsSlice } from '@/redux/config/residentListSettings';
import { useLoading } from '@/contexts/LoadingContext';
import { navSelector, navSlice } from '@/redux/config/leftnav';
import { selfDataSelector } from '@/redux/config/selfData';
import { useSortResidents } from '@/hooks/useSortResidents';
import { SortType } from '@/types/components';
import { BatteryAlertModal } from '@/components/batteryAlertModal';
import { useFetchDFreeGraphList } from '@/hooks/multi/useFetchDFreeGraphList';
import { IMdResident } from '@/types/api/medical/getResidents';
import { color } from '@/styles';
import { DFREE_GRAPH_VIEW_TYPE } from '@/constants';
import { Button } from '@mui/material';
import { isValueView } from '@/utils/dfreeGraph';
import { RecordDialog } from '@/components/careRecord/recordDialog';
import { MedicalRecordModal } from '@/components/careRecord/medicalRecordModal';
import DfreeGraphListItem from './dfreeGraphListItem';

const Container = css`
  padding: 0 16px;
`;

const SwitchDisplayButton = css`
  display: flex;
  align-items: center;
  column-gap: 4px;
`;

const EmptyResidentContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 16px;
`;

const BatteryAlertModalMessage = css`
  color: ${color.dark_gray};
`;

export const MedicalDfreeGraphList: React.FC = () => {
  const dispatch = useDispatch();
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { badge } = useSelector(navSelector);
  const residentListSettings = useSelector(residentListSettingsSelector);
  const self = useSelector(selfDataSelector);
  const [resident, setResident] = React.useState<IMdResident>();
  const { sortedResidents, sortResidents } = useSortResidents<IMdResident>();
  const { fetchMdDFreeGrapListData } = useFetchDFreeGraphList();
  const [batteryAlertResident, setBatteryAlertResident] = React.useState<IMdResident | null>(null);

  const [dateRange, setDateRange] = React.useState<{
    start: string;
    end: string;
  }>({
    start: formatUtcFetchTimestamp(dayjs(new Date()).subtract(5, 'hour')),
    end: formatUtcFetchTimestamp(dayjs(new Date())),
  });
  const { showLoading, hideLoading } = useLoading();
  const { t } = useTranslation();

  const createFilterType = (condition: 'all' | 'deviceAssigned' | 'deviceAttached' | 'unit') => {
    if (['deviceAssigned', 'deviceAttached'].includes(condition)) {
      return condition as 'deviceAssigned' | 'deviceAttached';
    } else return null;
  };

  const fetchResidents = React.useCallback(
    async (unitId?: number | null, condition?: 'deviceAssigned' | 'deviceAttached' | null, sortType?: SortType) => {
      const start = formatUtcFetchTimestamp(dayjs(new Date()).subtract(5, 'hour'));
      const end = formatUtcFetchTimestamp(dayjs(new Date()));
      setDateRange({ start, end });
      showLoading();

      try {
        const residents: any[] = await fetchMdDFreeGrapListData(unitId, condition ?? null, start, end);

        // 入居者一覧を並び替え
        sortResidents(residents, sortType ?? residentListSettings.sortType);
        hideLoading();

        // 本体機器異常検知
        setBatteryAlertResident(residents.find((v) => v.batteryAlertNotification));
      } catch (error: any) {
        console.error('fetchResidents error', error);
        hideLoading();
      }
    },
    [sortedResidents, residentListSettings, nursingHomeId]
  );

  const UpdateBatteryAlert = async (residentId?: number) => {
    setBatteryAlertResident(null);
    if (residentId) {
      await api.put('/residents/:residentId/notifications/covers', {
        residentId: residentId,
        uncaredBatteryAlert: false,
      });
      fetchResidents(
        residentListSettings.unitId,
        createFilterType(residentListSettings.conditions),
        residentListSettings.sortType
      );
    }
  };

  const handleOpenRecordModal = React.useCallback((resident: IMdResident) => {
    setResident(resident);
  }, []);

  const handleCloseRecordModal = React.useCallback(() => {
    setResident(undefined);
  }, []);

  const handleUpdate = React.useCallback(() => {
    fetchResidents(
      residentListSettings.unitId,
      createFilterType(residentListSettings.conditions),
      residentListSettings.sortType
    );
  }, [residentListSettings, nursingHomeId]);

  React.useEffect(() => {
    if (!self.selfUser.id) return;
    let unitId = null;

    let conditions =
      (localStorage.getItem('condition') as 'all' | 'deviceAssigned' | 'deviceAttached' | 'unit') ?? 'all';
    // 前回と異なるIDの場合はデフォルト値
    if (self.selfUser.id !== Number(localStorage.getItem('self')) && !self.selfUser.nursingHomeUnitId) {
      unitId = null;
      conditions = 'all';
    } else if (self.selfUser.nursingHomeUnitId) {
      unitId = self.selfUser.nursingHomeUnitId;
    } else if (!localStorage.getItem('unit') || localStorage.getItem('unit') === 'null') {
      unitId = null;
    } else {
      unitId = localStorage.getItem('unit');
    }
    fetchResidents(unitId ? +unitId : null, createFilterType(conditions));
  }, [self.selfUser.id, nursingHomeId]);

  React.useEffect(() => {
    const timer = setInterval(() => {
      handleUpdate();
    }, 1000 * 60 * 5);
    return () => {
      clearInterval(timer);
    };
  }, [residentListSettings, nursingHomeId]);

  // 通知アラートの消去とバッヂカウントの更新処理
  const handleResetNotification = React.useCallback(
    async (residentId: number, coverTypes?: string[]) => {
      let params = { residentId } as {
        residentId: number;
        uncaredBigBladder?: boolean;
        uncaredUrination?: boolean;
        uncaredGasPooped?: boolean;
        uncaredGasPrediction?: boolean;
        uncaredGotUp?: boolean;
        uncaredSensorError?: boolean;
        uncaredUrineReset?: boolean;
        uncaredZeroReset?: boolean;
        uncaredUpsideDown?: boolean;
        uncaredMoveUpperAdvice?: boolean;
        uncaredMoveLowerAdvice?: boolean;
      };

      const coverTypeMap: Record<string, keyof typeof params> = {
        bigBladder: 'uncaredBigBladder',
        urination: 'uncaredUrination',
        gasPooped: 'uncaredGasPooped',
        gasPrediction: 'uncaredGasPrediction',
        gotUp: 'uncaredGotUp',
        sensorError: 'uncaredSensorError',
        urineReset: 'uncaredUrineReset',
        zeroReset: 'uncaredZeroReset',
        upsideDown: 'uncaredUpsideDown',
        moveUpperAdvice: 'uncaredMoveUpperAdvice',
        moveLowerAdvice: 'uncaredMoveLowerAdvice',
      };

      const selectedCoverTypes = coverTypes ?? Object.keys(coverTypeMap);
      params = selectedCoverTypes.reduce((acc, type) => {
        const paramKey = coverTypeMap[type] as keyof typeof params;
        return paramKey ? { ...acc, [paramKey]: false } : acc;
      }, params);

      await api.put('/residents/:residentId/notifications/covers', params);
      if (badge.resident > 0) {
        dispatch(navSlice.actions.setResidentBadge(-1));
      }
      fetchResidents(residentListSettings.unitId, createFilterType(residentListSettings.conditions));
    },
    [residentListSettings, badge, nursingHomeId]
  );

  return (
    <div css={Container}>
      <ResidentListHeader
        title={t('dfreeGraph', 'DFreeグラフ')}
        conditions={residentListSettings.conditions}
        sortType={residentListSettings.sortType}
        unitId={residentListSettings.unitId}
        onUpdate={handleUpdate}
        onChangeListSettings={fetchResidents}
        rightElement={
          <Button
            onClick={() =>
              dispatch(
                residentListSettingsSlice.actions.setViewType(
                  isValueView(residentListSettings.viewType)
                    ? DFREE_GRAPH_VIEW_TYPE.Volume
                    : DFREE_GRAPH_VIEW_TYPE.Value
                )
              )
            }
            css={SwitchDisplayButton}
          >
            <span style={{ color: color.black }}>{t('SwitchDisplay', '表示切替')}</span>
            <img src='/images/icons/switch-display.svg' width='18' height='18' alt={t('SwitchDisplay', '表示切替')} />
          </Button>
        }
      />
      {sortedResidents && sortedResidents.length > 0 ? (
        <>
          {sortedResidents.map((resident, i) => (
            <DfreeGraphListItem
              key={resident.id}
              resident={resident}
              dateRange={dateRange}
              viewType={residentListSettings.viewType}
              openRecordModal={() => handleOpenRecordModal(resident)}
              resetNotification={(coverType?: string) =>
                handleResetNotification(resident.id, coverType ? [coverType] : undefined)
              }
            />
          ))}
        </>
      ) : (
        <div css={EmptyResidentContainer}>{t('thereAreNoResidents', '入居者がいません')}</div>
      )}
      {resident && (
        <RecordDialog isOpen={!!resident} dialogWidth={'640px'} onClose={handleCloseRecordModal}>
          <MedicalRecordModal
            resident={resident}
            resetNotification={handleResetNotification}
            onClose={handleCloseRecordModal}
            onUpdate={handleUpdate}
          />
        </RecordDialog>
      )}
      <BatteryAlertModal
        isOpen={!!batteryAlertResident}
        name={batteryAlertResident?.name}
        title={t('anAbnormalityHasBeenDetectedInTheDFreeDevice', 'DFree機器本体の異常を検知しました。')}
        onClose={() => {
          UpdateBatteryAlert(batteryAlertResident?.id);
        }}
      >
        <div css={BatteryAlertModalMessage}>
          {t('RemoveDFreeFromYourBodyAndTurnItOff', 'DFreeを身体から取り外し、電源を切ってください。')}
        </div>
      </BatteryAlertModal>
    </div>
  );
};
